<template>
    <div>
        <div v-if="loading" class="loader-container">
            <div class="loader"></div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <card>
                    <template slot="header">
                        <h3 class="card-title">Kretanje kontrolora</h3>
                    </template>

                    <!-- FILTERI -->
                    <div class="row">
                        <div class="col-md-3">
                            <h4 slot="header" class="card-title">Početak</h4>
                            <base-input>
                                <el-date-picker type="datetime" placeholder="Početak" value-format="timestamp"
                                    format="dd.MM.yyyy. HH:mm:ss" v-model="filter.startStamp">
                                </el-date-picker>
                            </base-input>
                        </div>
                        <div class="col-md-3">
                            <h4 slot="header" class="card-title">Kraj</h4>
                            <base-input>
                                <el-date-picker type="datetime" placeholder="Kraj" value-format="timestamp"
                                    format="dd.MM.yyyy. HH:mm:ss" v-model="filter.endStamp">
                                </el-date-picker>
                            </base-input>
                        </div>
                        <div class="col-md-3">
                            <h4 slot="header" class="card-title">Kontrolor</h4>
                            <base-input>
                                <el-select v-model="filter.employeeId" class="select-primary" name="employee" clearable>
                                    <el-option v-for="emp in employees" class="select-primary"
                                        :style="emp.active ? 'color: green;' : 'color:red'"
                                        :label="emp.active ? emp.name + ' ' + emp.lastName + ' AKTIVAN' : emp.name + ' ' + emp.lastName + ' NEAKTIVAN'"
                                        :value="emp.id" :key="emp.id"></el-option>
                                </el-select>
                            </base-input>
                        </div>
                        <div class="col-md-3">
                            <h4 slot="header" class="card-title">&nbsp;</h4>
                            <base-button class="mt-3" native-type="submit" type="primary"
                                @click="getEmployeeLocations()">Pretraži</base-button>
                        </div>
                    </div>
                </card>
            </div>

            <!-- MAPA -->
            <div class="col-12">
                <card>
                    <template slot="header">
                        <h4 class="card-title">Mapa kretanja ({{ locations.length }} tačaka)</h4>
                    </template>
                    <l-map style="height: 600px; width: 100%;" :zoom="zoom" :center="center" ref="map">
                        <l-tile-layer :url="mapUrl" :attribution="attribution" />

                        <!-- Polyline za putanju -->
                        <l-polyline v-if="polylinePoints.length > 1" :lat-lngs="polylinePoints" :color="'#3498db'"
                            :weight="3"></l-polyline>

                        <!-- Markeri za svaku lokaciju -->
                        <l-marker v-for="(loc, index) in locations" :key="index" :lat-lng="[loc.lat, loc.lon]"
                            :icon="getMarkerIcon(index)">
                            <l-popup>
                                <strong>{{ getEmployeeName(loc.employeeId) }}</strong><br>
                                Vreme: {{ formatDate(loc.stampLong) }}<br>
                                Tačka: {{ index + 1 }} / {{ locations.length }}
                            </l-popup>
                        </l-marker>

                        <!-- Start marker -->
                        <l-marker v-if="locations.length > 0" :lat-lng="[locations[0].lat, locations[0].lon]"
                            :icon="startIcon">
                            <l-popup>
                                <strong>START</strong><br>
                                {{ getEmployeeName(locations[0].employeeId) }}<br>
                                {{ formatDate(locations[0].stampLong) }}
                            </l-popup>
                        </l-marker>

                        <!-- End marker -->
                        <l-marker v-if="locations.length > 1"
                            :lat-lng="[locations[locations.length - 1].lat, locations[locations.length - 1].lon]"
                            :icon="endIcon">
                            <l-popup>
                                <strong>KRAJ</strong><br>
                                {{ getEmployeeName(locations[locations.length - 1].employeeId) }}<br>
                                {{ formatDate(locations[locations.length - 1].stampLong) }}
                            </l-popup>
                        </l-marker>
                    </l-map>
                </card>
            </div>

            <!-- Tabela sa lokacijama -->
            <div class="col-12">
                <card>
                    <template slot="header">
                        <h4 class="card-title">Lista lokacija</h4>
                    </template>
                    <el-table :data="locations" v-loading="tableLoading" max-height="400">
                        <el-table-column min-width="50" label="#" type="index"></el-table-column>
                        <el-table-column min-width="140" prop="stampLong" label="Datum/Vreme">
                            <template slot-scope="scope">
                                <span>{{ formatDate(scope.row.stampLong) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column min-width="120" prop="employeeId" label="Kontrolor">
                            <template slot-scope="scope">
                                <span>{{ getEmployeeName(scope.row.employeeId) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column min-width="100" prop="lat" label="Latitude">
                            <template slot-scope="scope">
                                <span>{{ scope.row.lat.toFixed(6) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column min-width="100" prop="lon" label="Longitude">
                            <template slot-scope="scope">
                                <span>{{ scope.row.lon.toFixed(6) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column min-width="80" label="Akcije">
                            <template slot-scope="scope">
                                <base-button type="info" size="sm" class="btn-link"
                                    @click="centerOnLocation(scope.row)">
                                    <i class="tim-icons icon-zoom-split"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </card>
            </div>
        </div>
    </div>
</template>

<script>
import { Table, TableColumn, DatePicker, Select, Option } from 'element-ui';
import axios from '@/util/axiosinstance';
import { LMap, LTileLayer, LMarker, LPopup, LPolyline } from "vue2-leaflet";
import 'leaflet/dist/leaflet.css';
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [DatePicker.name]: DatePicker,
        [Option.name]: Option,
        [Select.name]: Select,
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
        LPolyline
    },
    data() {
        const now = new Date();
        const startStamp = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).getTime();
        const endStamp = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).getTime();

        return {
            url: 'https://budva.scancar.org:8443/kuca',
            loading: false,
            tableLoading: false,
            locations: [],
            employees: [],
            filter: {
                startStamp: startStamp,
                endStamp: endStamp,
                employeeId: null
            },
            center: [42.4533932, 18.5291398], // HERCEG NOVI
            zoom: 15,
            mapUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',

            // Ikone za markere
            startIcon: L.divIcon({
                html: '<i class="fas fa-play-circle" style="font-size: 30px; color: green;"></i>',
                iconSize: [30, 30],
                className: 'custom-div-icon'
            }),
            endIcon: L.divIcon({
                html: '<i class="fas fa-stop-circle" style="font-size: 30px; color: red;"></i>',
                iconSize: [30, 30],
                className: 'custom-div-icon'
            }),
            defaultIcon: L.divIcon({
                html: '<i class="fas fa-circle" style="font-size: 10px; color: #3498db;"></i>',
                iconSize: [10, 10],
                className: 'custom-div-icon'
            })
        };
    },
    computed: {
        polylinePoints() {
            return this.locations.map(loc => [loc.lat, loc.lon]);
        }
    },
    methods: {
        getMarkerIcon(index) {
            // Prvi i poslednji marker imaju posebne ikone (start/end)
            if (index === 0 || index === this.locations.length - 1) {
                return L.divIcon({
                    html: '',
                    iconSize: [0, 0],
                    className: 'hidden-marker'
                });
            }
            return this.defaultIcon;
        },
        getEmployeeName(employeeId) {
            const emp = this.employees.find(e => e.id === employeeId);
            if (emp) {
                return emp.name + ' ' + emp.lastName;
            }
            return employeeId || '-';
        },
        formatDate(timestamp) {
            if (!timestamp) return '';
            return new Date(timestamp).toLocaleString("de-DE");
        },
        centerOnLocation(loc) {
            this.center = [loc.lat, loc.lon];
            this.zoom = 18;
        },
        fitBounds() {
            if (this.locations.length > 0 && this.$refs.map) {
                const bounds = L.latLngBounds(this.polylinePoints);
                this.$refs.map.mapObject.fitBounds(bounds, { padding: [50, 50] });
            }
        },
        getEmployees() {
            const body = {
                onlyActive: false
            };
            axios.post(this.url + '/employee/getEmployees', body).then(result => {
                if (result.data.result === 'ok') {
                    this.employees = result.data.employees;
                }
            });
        },
        getEmployeeLocations() {
            if (!this.filter.employeeId) {
                this.$notify({
                    message: 'Morate izabrati kontrolora',
                    timeout: 3000,
                    icon: 'tim-icons icon-bell-55',
                    horizontalAlign: "right",
                    verticalAlign: "top",
                    type: 'warning'
                });
                return;
            }

            // Validacija perioda - max 48 sati
            const maxPeriodMs = 48 * 60 * 60 * 1000; // 48 sati u milisekundama
            const periodMs = this.filter.endStamp - this.filter.startStamp;

            if (periodMs > maxPeriodMs) {
                this.$notify({
                    message: 'Period ne može biti veći od 48 sati',
                    timeout: 3000,
                    icon: 'tim-icons icon-bell-55',
                    horizontalAlign: "right",
                    verticalAlign: "top",
                    type: 'warning'
                });
                return;
            }

            if (periodMs < 0) {
                this.$notify({
                    message: 'Datum početka mora biti pre datuma kraja',
                    timeout: 3000,
                    icon: 'tim-icons icon-bell-55',
                    horizontalAlign: "right",
                    verticalAlign: "top",
                    type: 'warning'
                });
                return;
            }

            this.tableLoading = true;
            this.loading = true;

            const body = {
                start: this.filter.startStamp,
                end: this.filter.endStamp,
                employeeId: this.filter.employeeId
            };

            axios.post(this.url + '/employee/getEmployeeLocations', body).then(result => {
                this.tableLoading = false;
                this.loading = false;

                if (result.data.result === 'ok') {
                    this.locations = result.data.employeeLocations || [];

                    // Centriraj mapu na putanju
                    this.$nextTick(() => {
                        this.fitBounds();
                    });

                    if (this.locations.length === 0) {
                        this.$notify({
                            message: 'Nema lokacija za izabrani period',
                            timeout: 3000,
                            icon: 'tim-icons icon-bell-55',
                            horizontalAlign: "right",
                            verticalAlign: "top",
                            type: 'info'
                        });
                    }
                } else {
                    this.$notify({
                        message: result.data.reason || 'Greška prilikom učitavanja',
                        timeout: 3000,
                        icon: 'tim-icons icon-bell-55',
                        horizontalAlign: "right",
                        verticalAlign: "top",
                        type: 'danger'
                    });
                }
            }).catch(error => {
                this.tableLoading = false;
                this.loading = false;
                this.$notify({
                    message: 'Greška: ' + error.message,
                    timeout: 3000,
                    icon: 'tim-icons icon-bell-55',
                    horizontalAlign: "right",
                    verticalAlign: "top",
                    type: 'danger'
                });
            });
        },
        getCenter() {
            axios.get(this.url + '/reports/getCenter').then(result => {


                if (result.data.result === 'ok') {

                    this.center = JSON.parse(result.data.center)
                } else {
                    this.$notify({
                        message:
                            result.data.reason,
                        timeout: 3000,
                        icon: 'tim-icons icon-bell-55',
                        horizontalAlign: "right",
                        verticalAlign: "top",
                        type: 'danger'
                    });
                }
            })
        }
    },
    mounted() {
        this.getCenter()
        this.getEmployees();
    }
};
</script>

<style>
.leaflet-container {
    height: 100%;
    width: 100%;
}

.custom-div-icon {
    background: transparent;
    border: none;
}

.hidden-marker {
    display: none;
}

.loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 9999;
}

.loader {
    border: 8px solid #f3f3f3;
    border-top: 8px solid #3498db;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>